import { useState } from 'react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { AlertCircle, Upload, X } from 'lucide-react';
import { retailer, getErrorMessage } from '../../utils/api';

const MAX_FILES = 5;
const MAX_BYTES = 10 * 1024 * 1024;
const ACCEPT = '.jpg,.jpeg,.png,.pdf';
const ACCEPT_RE = /\.(jpe?g|png|pdf)$/i;

/**
 * DisputeSubmitModal — retailer opens a dispute against a specific
 * refund_adjustment. Enforces the same client-side constraints the
 * backend does (max 5 files, 10 MB each, jpg/png/pdf) so the retailer
 * gets fast feedback before the request round-trips.
 *
 * Props:
 *   open, onOpenChange, adjustment (the row being disputed), onFiled
 */
export default function DisputeSubmitModal({ open, onOpenChange, adjustment, onFiled }) {
  const [comment, setComment] = useState('');
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const _reset = () => {
    setComment('');
    setFiles([]);
  };

  const _close = () => {
    _reset();
    onOpenChange(false);
  };

  const _addFiles = (list) => {
    const incoming = Array.from(list || []);
    for (const f of incoming) {
      if (!ACCEPT_RE.test(f.name)) {
        toast.error(`${f.name}: only jpg/png/pdf are allowed`);
        return;
      }
      if (f.size > MAX_BYTES) {
        toast.error(`${f.name}: max 10 MB`);
        return;
      }
    }
    const next = [...files, ...incoming].slice(0, MAX_FILES);
    if (files.length + incoming.length > MAX_FILES) {
      toast.error(`Max ${MAX_FILES} files`);
    }
    setFiles(next);
  };

  const _remove = (idx) => setFiles(files.filter((_, i) => i !== idx));

  const _submit = async () => {
    if (!comment.trim() || comment.trim().length < 10) {
      toast.error('Please describe why you dispute this adjustment (at least 10 characters).');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await retailer.fileDispute({
        adjustment_id: adjustment.id,
        retailer_comment: comment.trim(),
        retailer_evidence_urls: [],
      });
      const disputeId = data?.dispute?.id;
      if (files.length && disputeId) {
        const fd = new FormData();
        files.forEach((f) => fd.append('files', f));
        await retailer.uploadDisputeEvidence(disputeId, fd);
      }
      toast.success('Dispute filed. DealShaq will review shortly.');
      onFiled && onFiled();
      _close();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to file dispute'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(true) : _close())}>
      <DialogContent className="max-w-lg" data-testid="dispute-modal">
        <DialogHeader>
          <DialogTitle>Dispute this adjustment</DialogTitle>
          <DialogDescription>
            You have 7 days from the monthly rollup statement to file a dispute. Provide
            a clear explanation and any supporting evidence.
          </DialogDescription>
        </DialogHeader>

        {adjustment && (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 flex gap-2">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>
              <div className="font-medium">Adjustment #{adjustment.id?.slice(0, 8)}</div>
              <div className="text-xs">
                Reason: <span className="font-semibold">{adjustment.reason}</span> ·
                Withheld: <span className="font-semibold">${Number(adjustment.withheld_amount || 0).toFixed(2)}</span>
              </div>
            </div>
          </div>
        )}

        <div>
          <label className="text-sm font-medium text-gray-700 mb-1 block">
            Your comment <span className="text-red-600">*</span>
          </label>
          <Textarea
            data-testid="dispute-comment"
            rows={4}
            placeholder="Explain why this adjustment is incorrect (min 10 characters)…"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700 mb-1 block">
            Evidence (optional — up to {MAX_FILES} files · 10 MB each · jpg/png/pdf)
          </label>
          <label
            htmlFor="dispute-file-input"
            className="flex items-center justify-center gap-2 py-6 border-2 border-dashed border-gray-300 rounded-md hover:border-emerald-500 cursor-pointer"
            data-testid="dispute-file-drop"
          >
            <Upload className="w-4 h-4 text-gray-500" />
            <span className="text-sm text-gray-600">Click to upload files</span>
            <input
              id="dispute-file-input"
              type="file"
              multiple
              accept={ACCEPT}
              className="hidden"
              onChange={(e) => _addFiles(e.target.files)}
              data-testid="dispute-file-input"
            />
          </label>
          {files.length > 0 && (
            <ul className="mt-2 space-y-1">
              {files.map((f, i) => (
                <li key={i} className="flex items-center justify-between text-xs bg-gray-50 rounded px-2 py-1">
                  <span className="truncate">{f.name} · {(f.size / 1024).toFixed(0)} KB</span>
                  <button
                    onClick={() => _remove(i)}
                    className="text-red-500 hover:text-red-700"
                    data-testid={`dispute-file-remove-${i}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={_close} disabled={submitting} data-testid="dispute-cancel-btn">
            Cancel
          </Button>
          <Button
            onClick={_submit}
            disabled={submitting}
            data-testid="dispute-submit-btn"
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {submitting ? 'Submitting…' : 'File Dispute'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
